import React, { useState, useEffect, useRef } from "react";
import { Auth } from "../../Common/lib/auth";
import { API } from "../../Common/lib/api";
import { HiFilter, HiSearch } from "react-icons/hi";
/**
 * Search bar component. Fetches projects to be displayed on the data catalogue page.
 *
 * @param {*} props Contains 'filters', 'setProjects', 'setProjectCount', 'activeSortOption'.
 * @returns React Component
 */
export default function SearchBarSection(props) {
  const [searchBarValue, setSearchBarValue] = useState(getInitialSearchValue());
  const [displayedSearchBarValue, setDisplayedSearchBarValue] = useState(getInitialSearchValue());
  const filters = props.filters;

  const searchBarRef = useRef(0);

  /**
   * Sets initial 'searchBarValue' to the search value found in the URL.
   * @returns {String} Initial value of 'searchBarValue'.
   */
  function getInitialSearchValue() {
    const urlParams = new URLSearchParams(window.location.search);
    let search = urlParams.get("search")
    return search === null ? "" : search;
  }

  //Fetch records on page load, project page change (pagination), or sort option change
  useEffect(() => {
    searchProjectsQuery();
  }, [props.currentPage, props.activeSortOption]);

  // Search projects 1 second after filters have been updated
  useEffect(() => {
    // debounce to prevent spam querying if user is adding/removing filters quickly
    const getData = setTimeout(() => {
      searchProjectsQuery();
    }, 1000)

    return () => clearTimeout(getData)
  }, [filters])



  /**
   * Puts together an array of objects based on the publisher, research platform, and keywords selection to be used in the GraphQL search filter.
   *
   * @returns Array of objects containing search filter conditions for publisher, research platform, and keywords.
   */
  function getMainFilters() {
    let mainFilter = [];

    if (filters.publisher.length !== 0) {
      mainFilter.push({ stakeholders: { matchPhrase: filters.publisher[0] } });
    }

    if (filters.researchPlatform.length !== 0) {
      mainFilter.push({ vessels: { matchPhrase: filters.researchPlatform[0] } });
    }

    if (filters.keywords.length !== 0) {
      for (var i = 0; i < filters.keywords.length; i++) {
        mainFilter.push({ keywords: { matchPhrase: filters.keywords[i] } });
      }
    }

    return [...mainFilter];
  }



  /**
   * Puts together an array of objects based on the publication year selection to be used in the GraphQL search filter.
   *
   * @returns Array of objects containing search filter conditions for publication years.
   */
  function getPublicationYearFilters() {
    let publicationYearFilter = [];

    if (filters.publicationYear.length !== 0) {
      for (var i = 0; i < filters.publicationYear.length; i++) {
        publicationYearFilter.push({ publishedAt: { gte: new Date(filters.publicationYear[i]).getTime(), lte: new Date(filters.publicationYear[i]).getTime() + 31536000000 } });
      }
    }
    return [...publicationYearFilter];
  }



  /**
   * Puts together an object based on the duration selection to be used in the GraphQL search filter.
   *
   * @returns Object with duration properties for the search filter.
   */
  function getDurationFilters() {
    let durationFilter = {};
    if (filters.duration.start !== "") {
      durationFilter.startDate = { gte: new Date(filters.duration.start).getTime() };
    }
    if (filters.duration.end !== "") {
      durationFilter.endDate = { lte: new Date(filters.duration.end).getTime() };
    }
    return durationFilter;
  }



  /**
   * Puts together an object based on the selection made on the map filter. Created array checks if the rectangle selected on the map overlaps with any rectangles stored in the database.
   *
   * @returns Array of objects containing search filter conditions for map location.
   */
  function getMapFilters() {
    let mapFilter = [];

    let westLongitude = parseFloat(filters.coordinates.westLongitude);
    let eastLongitude = parseFloat(filters.coordinates.eastLongitude);

    // Convert negative longitudes to positive - necessary to determine if crossing antimeridian.
    if (filters.coordinates.westLongitude < 0) {
      westLongitude += 360;
      eastLongitude += 360;
    }
    if (filters.coordinates.eastLongitude < 0) {
      westLongitude += 360;
      eastLongitude += 360;
    }

    if (filters.coordinates.eastLongitude !== "") {
    // Check if the bounding box crosses the antimeridian
    if (westLongitude > eastLongitude) {
      mapFilter.push({
        eastLongitude: {
            range: [filters.coordinates.eastLongitude, filters.coordinates.westLongitude],
        },
      });

      mapFilter.push({
        westLongitude: {
            range: [filters.coordinates.westLongitude, filters.coordinates.eastLongitude],
        },
      });

      mapFilter.push({ northLatitude: { lte: filters.coordinates.southLatitude } });
      mapFilter.push({ southLatitude: { gte: filters.coordinates.northLatitude } });
    } else {
      // Bounding box does not cross the antimeridian
      mapFilter.push({ eastLongitude: { lte: filters.coordinates.westLongitude } });
      mapFilter.push({ westLongitude: { gte: filters.coordinates.eastLongitude } });
      mapFilter.push({ northLatitude: { lte: filters.coordinates.southLatitude } });
      mapFilter.push({ southLatitude: { gte: filters.coordinates.northLatitude } });
    }

  }
    return [...mapFilter]; // Combine filters with logical AND
}

  /**
   * Retrieves filtered projects based on the filters selected by the user and assigns them to the 'projects' state variable.
   * Puts together a 'searchFilter' object with all the filter options that the user has selected and then uses it for the OpenSearch GraphQL query.
   */
  async function searchProjectsQuery(searchValue = searchBarValue) {
    //Set displayed search to the searchBarValue state if it was already set, otherwise set to searchValue parameter.
    setDisplayedSearchBarValue(searchValue)

    props.setLoading(true);
    let searchFilter = {};

    //Use the searchValue paremeter if one was given, otherwise use the searchBarValueState.
    if (searchValue) {
      //Search filter when the search bar is populated.
      searchFilter = {
        ...getDurationFilters(),
        and: [
          {
            or: [
              { abstract: { matchPhrasePrefix: searchValue} },
              { title: { matchPhrasePrefix: searchValue} },
            ]
          },
        ]
      }
      //Check if filters were selected before trying to populate the 'and' & 'or' properties of searchFilter. Adding empty 'and' & 'or' properties causes the filter to not work.
      if (getMapFilters().length !== 0) {
        searchFilter.not = { or: [...getMapFilters()] };
        searchFilter.and.push({ category: { eq: "project" } });
      }
      if (getMainFilters().length !== 0) {
        searchFilter.and.push(...getMainFilters());
      }
      if (getPublicationYearFilters().length !== 0) searchFilter.or = [...getPublicationYearFilters()];
    }
    else {
      if (getMainFilters().length === 0 && getPublicationYearFilters().length === 0 && Object.keys(getDurationFilters()).length === 0 && getMapFilters().length === 0) {
        //Search filter when no filters were selected and search bar isn't populated. Fetch random projects.
        searchFilter = {}
      }
      else {
        //Search filter when filters were selected and search bar isn't populated.
        searchFilter = {
          ...getDurationFilters(),
        }

        //Check if filters were selected before trying to populate the 'and' & 'or' properties of searchFilter. Adding empty 'and' & 'or' properties causes the filter to not work.
        if (getMapFilters().length !== 0) {
          searchFilter.not = { or: [...getMapFilters()] };
        }
        if (getMainFilters().length !== 0) {
          searchFilter.and instanceof Array ? searchFilter.and.push(...getMainFilters()) : searchFilter.and = [...getMainFilters()];
        }
        if (getPublicationYearFilters().length !== 0) searchFilter.or = [...getPublicationYearFilters()];
      }
    }

    // Will only show projects that are published
    searchFilter.publishStatus = { eq: "published" };



    //After the 'searchFilter' object is put together call the API.
    try {
      const params = {
        offset: props.currentPage * 8,
        limit: 8,
        ordering: `${props.activeSortOption.direction === 'desc' ? '-' : ''}${props.activeSortOption.field}`,
      };

      // Add search term
      if (searchValue) {
        params.q = searchValue;
      }

      // Add publish status
      params.publishStatus = "published";

      // Add publisher filter (only first one for now)
      if (filters.publisher && filters.publisher.length > 0) {
        params.publisher__icontains = filters.publisher[0];
      }

      // Add research platform filter (only first one for now)
      if (filters.researchPlatform && filters.researchPlatform.length > 0) {
        params.vessels__icontains = filters.researchPlatform[0];
      }

      // Add keywords filter
      if (filters.keywords && filters.keywords.length > 0) {
        params.keywords__icontains = filters.keywords[0];
      }

      // Add publication year filters
      if (filters.publicationYear && filters.publicationYear.length > 0) {
        // Send all selected years as a comma-separated param to backend
        // Backend will parse and OR the year ranges
        params.publicationYear = filters.publicationYear.join(",");
      }

      // Add duration filters (start/end dates)
      if (filters.duration) {
        if (filters.duration.start) {
          params.start_date__gte = new Date(filters.duration.start).toISOString();
        }
        if (filters.duration.end) {
          params.end_date__lte = new Date(filters.duration.end).toISOString();
        }
      }

      // Add map/coordinate filters
      // For bounding box overlap, a project overlaps the selected area if:
      // - project.west_longitude <= selected.east_longitude (project's west is east of selected east)
      // - project.east_longitude >= selected.west_longitude (project's east is west of selected west)
      // - project.north_latitude >= selected.south_latitude (project's north is south of selected south)
      // - project.south_latitude <= selected.north_latitude (project's south is north of selected north)
      if (filters.coordinates && (
        filters.coordinates.westLongitude ||
        filters.coordinates.eastLongitude ||
        filters.coordinates.northLatitude ||
        filters.coordinates.southLatitude
      )) {
        if (filters.coordinates.westLongitude !== "" && filters.coordinates.eastLongitude !== "") {
          // Project's west edge must be <= selected area's east edge
          params.west_longitude__lte = parseFloat(filters.coordinates.eastLongitude);
          // Project's east edge must be >= selected area's west edge
          params.east_longitude__gte = parseFloat(filters.coordinates.westLongitude);
        }
        if (filters.coordinates.northLatitude !== "" && filters.coordinates.southLatitude !== "") {
          // Project's north edge must be >= selected area's south edge
          params.north_latitude__gte = parseFloat(filters.coordinates.southLatitude);
          // Project's south edge must be <= selected area's north edge
          params.south_latitude__lte = parseFloat(filters.coordinates.northLatitude);
        }
      }

      const projectsQuery = await API.listProjects(params);
      props.setProjects(projectsQuery.data.listProjects.items);
      props.setProjectCount(projectsQuery.data.listProjects.total || 0);
      props.setLoading(false);
    } catch (e) {
      props.setLoading(false);
    }
  };

  /**
   * Checks if the user is currently logged in and returns the right authentication mode to be used for GraphQL queries.
   *
   * @returns 'AMAZON_COGNITO_USER_POOLS' when the user is logged in. 'AWS_IAM' when the user is not logged in.
   */
  async function getAuthMode() {
    try {
      await Auth.currentAuthenticatedUser();
      return 'AMAZON_COGNITO_USER_POOLS';
    } catch {
      return 'AWS_IAM';
    }
  }

  /**
   * Handles search button and search input onKeyDown event for 'Enter'. Adds the search value to the URL and removes it from the URL when search value is empty.
   */
  function handleSearchButton() {
    //Set the searchBarValue state to the current value of the input
    setSearchBarValue(searchBarRef.current.value.trim());

    if (props.currentPage !== 0) {
      props.setCurrentPage(0)
    }
    else {
      searchProjectsQuery(searchBarRef.current.value.trim());
    }
    const url = new URL(window.location.href);
    url.searchParams.set("search", searchBarValue);
    window.history.replaceState(null, null, url);

    if (searchBarValue.trim() === "") {
      url.searchParams.delete("search");
      window.history.replaceState(null, null, url);
    }
  }
  return (
    <>
      <div className="pl-0 lg:pl-2 mb-2 ml-auto lg:w-3/4 w-full">
        <div className="flex justify-between">
          <div className="relative flex flex-1 items-center">
          <label htmlFor="searchbar" className="sr-only">Search Bar</label>
            <input
              id="searchbar"
              ref={searchBarRef}
              data-cy="search"
              type="search"
              className="py-2 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full pr-3 pl-12 bg-purple-white shadow-sm rounded-md border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="Search by title, abstract"
              value={displayedSearchBarValue}
              onChange={(e) => {
                setDisplayedSearchBarValue(e.target.value);
                if (e.target.value === "") handleSearchButton();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchButton()
                }
              }}
            />
            <div className="absolute m-3.5 text-purple-lighter">
              <HiSearch className="w-5 h-5 text-gray-400 dark:text-gray-200" />
            </div>
          </div>
          <button
            data-cy="search-submit"
            className="hidden sm:inline-flex text-white items-center justify-center ml-4 px-10 py-2 text-sm
                      leading-6 font-medium rounded-md bg-blue-700 hover:bg-blue-500 focus:outline-none
                      focus:shadow-outline-blue active:bg-blue-700 transition ease-in-out duration-150"
            onClick={() => { handleSearchButton() }}
          >
            Search
          </button>
          <button
            type="button"
            className="bg-gray-200 ml-4 border text-gray-600 hover:bg-gray-400 lg:hidden focus:outline-none inline-flex items-center rounded-md px-4 py-2 gap-x-2 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
            onClick={() => props.setMobileFiltersOpen(true)}
          >
            <span className="sr-only"> Filters </span>
            <HiFilter className="w-6 h-6"/>
            <span className="font-medium hidden sm:block"> Filters </span>
          </button>
        </div>
      </div>
    </>
  );
}
